import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import type { FC } from 'react';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import React, { useState, useEffect } from 'react';
import { InformationCircleLineSIcon } from '@alfalab/icons-glyph/InformationCircleLineSIcon';
import { WrappedCheckboxDesktop as Checkbox } from '@alfa-bank/site-checkbox/checkbox-desktop';
import cn from 'classnames';
import { useForm, useField } from 'react-final-form';

import styles from './styles/styles.css';
import type { TBundleFormCheckbox } from './models';
import { getFullProductsFieldName, isAgeWithinInterval } from './helpers';

export const DcFormBundleCheckbox: FC<TBundleFormCheckbox> = ({
    Text,
    Tooltip,
    additionalProducts,
    ageChecking,
    bottomSheets,
    caption,
    className,
    hintText,
    initialValue,
    isMobile,
    label,
    name = 'additionalProducts',
}) => {
    const [isManualChecked, setIsManualChecked] = useState(false);
    const formApi = useForm();
    const {
        input: { value: passportBirthDate },
    } = useField('passportBirthDate', { subscription: { value: true } });

    const allAdditionalProductsFieldName = getFullProductsFieldName(name);
    const mobileProps = isMobile
        ? {
              title: bottomSheets?.title ? (
                  <Text styler={{ margin: 0, padding: 0 }} view="small">
                      {bottomSheets?.title}
                  </Text>
              ) : undefined,
          }
        : null;

    useEffect(() => {
        formApi.change(allAdditionalProductsFieldName, initialValue || []);
        // выполняется только на монтирование
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Field<boolean>
            name={name}
            render={({ input, meta }) => {
                let isChecked = input.value || false;
                const isAgeChecking = !!ageChecking?.length;

                if (!isManualChecked && isAgeChecking) {
                    const [startAge, endAge] = ageChecking;
                    isChecked = isAgeWithinInterval(passportBirthDate, [startAge, endAge]);
                }

                if (isChecked) {
                    actions.updateAction(meta, { [name]: additionalProducts });
                } else {
                    actions.updateAction(meta, { [name]: '' });
                }

                return (
                    <Checkbox
                        caption={caption}
                        checked={isChecked}
                        className={className}
                        name="bundleCheckbox"
                        onChange={({ checked }) => {
                            actions.onChange(input, meta)(checked);
                            setIsManualChecked(true);

                            const allAdditionalProducts =
                                formApi.getState().values?.[allAdditionalProductsFieldName] || [];

                            if (checked) {
                                formApi.change(allAdditionalProductsFieldName, [
                                    ...allAdditionalProducts,
                                    additionalProducts,
                                ]);

                                return;
                            }

                            const currentProductIdx = allAdditionalProducts.findIndex(
                                (productId: string) => productId === additionalProducts
                            );

                            formApi.change(
                                allAdditionalProductsFieldName,
                                allAdditionalProducts.toSpliced(currentProductIdx, 1)
                            );
                        }}
                    >
                        <div className={cn(styles.checkboxWrapper, styles.external)}>
                            <Text styler={{ padding: 0, margin: 0 }} view="primary-medium">
                                {label}
                            </Text>
                            {hintText && (
                                <Tooltip
                                    arrowClassName={cn(styles.bundleArrow, styles.external)}
                                    content={hintText}
                                    contentClassName={cn(styles.hint, styles.external)}
                                    fallbackPlacements={['bottom', 'top']}
                                    hasCloser
                                    popoverClassName={cn(styles.bundlePopover, styles.external)}
                                    position="top"
                                    preventOverflow
                                    trigger="hover"
                                    view="hint"
                                    {...mobileProps}
                                >
                                    <InformationCircleLineSIcon
                                        className={cn(styles.tooltipContent, styles.external)}
                                        // Добавлено чтобы клик по тултипу не тригерил чекбокс
                                        onClick={event => event.preventDefault()}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </Checkbox>
                );
            }}
        />
    );
};
